import { useEffect, useRef, useCallback } from 'preact/hooks';
import type { SchemaTable, SchemaForeignKey } from '../../api/types';

interface ErdCanvasProps {
  tables: SchemaTable[];
}

interface Relationship {
  fromTable: string;
  fromCol: string;
  toTable: string;
  toCol: string;
}

const LINE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#10b981'];

export function ErdCanvas({ tables }: ErdCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Build FK lookup and relationship list
  const fkSet: Record<string, string> = {};
  const relationships: Relationship[] = [];

  for (const t of tables) {
    for (const fk of t.foreignKeys) {
      fkSet[`${t.name}.${fk.from}`] = `${fk.table}.${fk.to}`;
      relationships.push({ fromTable: t.name, fromCol: fk.from, toTable: fk.table, toCol: fk.to });
    }
  }

  const drawLines = useCallback(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    if (!svg || !canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    svg.setAttribute('width', String(canvas.scrollWidth));
    svg.setAttribute('height', String(canvas.scrollHeight));
    svg.style.width = `${canvas.scrollWidth}px`;
    svg.style.height = `${canvas.scrollHeight}px`;

    // Clear existing lines
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const fkCols = canvas.querySelectorAll<HTMLElement>('[data-col]');
    let colorIdx = 0;

    fkCols.forEach((colEl) => {
      const refDiv = colEl.nextElementSibling as HTMLElement | null;
      if (!refDiv || !refDiv.textContent?.includes('\u2192')) return;

      const refText = refDiv.textContent.replace('\u2192', '').trim();
      const targetEl = canvas.querySelector<HTMLElement>(`[data-col="${refText}"]`);
      if (!targetEl) return;

      const fromRect = colEl.getBoundingClientRect();
      const toRect = targetEl.getBoundingClientRect();

      let fromX = fromRect.right - canvasRect.left;
      let fromY = fromRect.top + fromRect.height / 2 - canvasRect.top;
      let toX = toRect.left - canvasRect.left;
      const toY = toRect.top + toRect.height / 2 - canvasRect.top;

      // If target is to the left, connect from left side
      if (toRect.left < fromRect.left) {
        fromX = fromRect.left - canvasRect.left;
        toX = toRect.right - canvasRect.left;
      }

      const color = LINE_COLORS[colorIdx % LINE_COLORS.length];
      colorIdx++;

      const midX = (fromX + toX) / 2;

      // Bezier curve path
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', `M${fromX},${fromY} C${midX},${fromY} ${midX},${toY} ${toX},${toY}`);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', color);
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('stroke-dasharray', '6 3');
      path.setAttribute('opacity', '0.5');
      svg.appendChild(path);

      // Dot at target end
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('cx', String(toX));
      dot.setAttribute('cy', String(toY));
      dot.setAttribute('r', '3');
      dot.setAttribute('fill', color);
      dot.setAttribute('opacity', '0.7');
      svg.appendChild(dot);
    });
  }, [tables]);

  useEffect(() => {
    const timer = setTimeout(drawLines, 50);
    window.addEventListener('resize', drawLines);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', drawLines);
    };
  }, [drawLines]);

  return (
    <div ref={canvasRef} style={{ position: 'relative', minHeight: '600px' }}>
      <svg
        ref={svgRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {tables.map((t) => (
          <div
            key={t.name}
            data-table={t.name}
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              minWidth: '220px',
              maxWidth: '280px',
              flex: '1 1 220px',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s, border-color 0.2s',
            }}
          >
            {/* Table header */}
            <div
              style={{
                padding: '10px 14px',
                background: 'var(--bg-surface)',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}
              >
                {t.name}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.65rem',
                  color: 'var(--text-tertiary)',
                  background: 'var(--bg-hover)',
                  padding: '2px 7px',
                  borderRadius: '10px',
                }}
              >
                {t.rowCount}
              </span>
            </div>

            {/* Columns */}
            <div style={{ padding: '4px 0' }}>
              {t.columns.map((col) => {
                const isPk = col.pk > 0;
                const colKey = `${t.name}.${col.name}`;
                const isFk = colKey in fkSet;
                const fkTarget = fkSet[colKey];

                return (
                  <div key={col.name}>
                    <div
                      data-col={colKey}
                      style={{
                        padding: '5px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.78rem',
                        borderLeft: `3px solid ${isPk ? 'var(--accent)' : isFk ? 'var(--blue)' : 'transparent'}`,
                        background: isPk
                          ? 'rgba(16,185,129,0.04)'
                          : isFk
                            ? 'rgba(59,130,246,0.04)'
                            : undefined,
                      }}
                    >
                      {/* Icon */}
                      {isPk ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--accent)"
                          stroke-width="2.5"
                          stroke-linecap="round"
                        >
                          <circle cx="12" cy="8" r="5" />
                          <path d="M12 13v8" />
                          <path d="M9 18h6" />
                        </svg>
                      ) : isFk ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--blue)"
                          stroke-width="2.5"
                          stroke-linecap="round"
                        >
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      ) : (
                        <span style={{ width: '12px' }} />
                      )}

                      {/* Column name */}
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontWeight: isPk ? 600 : 400,
                          color: 'var(--text-primary)',
                          flex: 1,
                        }}
                      >
                        {col.name}
                      </span>

                      {/* Type badge */}
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '0.65rem',
                          color: 'var(--text-tertiary)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.type || 'TEXT'}
                      </span>

                      {/* NOT NULL indicator */}
                      {col.notnull ? (
                        <span
                          style={{
                            fontSize: '0.55rem',
                            color: 'var(--red)',
                            fontWeight: 600,
                            letterSpacing: '0.03em',
                          }}
                        >
                          NN
                        </span>
                      ) : null}
                    </div>

                    {/* FK reference hint */}
                    {isFk && (
                      <div
                        style={{
                          padding: '0 14px 4px 37px',
                          fontSize: '0.65rem',
                          color: 'var(--blue)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {'\u2192'} {fkTarget}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
